import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AnalyticsEventsService } from '@techspert-io/analytics';
import { ExpertFile, FileType } from '@techspert-io/expert-files';
import { filter, finalize, takeUntil, tap } from 'rxjs/operators';
import { IExpert } from '../../../../../shared/models/expert.interface';
import {
  Add,
  Delete,
  ExpertFilesStateService,
  UpdateProgress,
} from '../../../../../shared/services/filestore-state.service';
import { FileStoreService } from '../../../../../shared/services/filestore.service';

export interface IExpertRenderFile
  extends
    Pick<ExpertFile, 'fileKey' | 'fileName' | 'fileExtension' | 'type'>,
    Partial<Pick<ExpertFile, 'fileId' | 'groupId'>> {}

export interface IExpertFileGrouping {
  groupId: string;
  label: string;
  files: IExpertRenderFile[];
}

@Component({
  selector: 'app-expert-files',
  templateUrl: './expert-files.component.html',
  styleUrls: ['./expert-files.component.scss'],
  providers: [ExpertFilesStateService],
  standalone: false,
})
export class ExpertFilesComponent implements OnInit, OnChanges {
  @Input() files: IExpertRenderFile[] = [];
  @Input() actions: IExpert['actions'] = [];
  @Input() filePrefix: string = '';

  @Output() replayInterview = new EventEmitter<string>();

  loadingFiles$ = this.filesState.state$;
  downloadedFiles: Record<string, boolean> = {};

  presentationalFiles: IExpertFileGrouping[] = [];
  uncategorisedFiles: IExpertRenderFile[] = [];

  constructor(
    private fileStore: FileStoreService,
    private filesState: ExpertFilesStateService,
    private analyticsEventsService: AnalyticsEventsService
  ) {}

  ngOnInit() {
    this.groupFiles();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.files || changes.actions) {
      this.groupFiles();
    }
  }

  downloadFile(file: IExpertRenderFile) {
    this.downloadFileImpl(file);
  }

  cancelDownload(fileName: string) {
    this.filesState.dispatch(new Delete(fileName));
  }

  onReplayInterviewClick(groupId: string) {
    this.replayInterview.emit(groupId);
  }

  private downloadFileImpl(file: IExpertRenderFile) {
    this.filesState.dispatch(new Add(file.fileKey));
    this.fileStore
      .downloadExpertFile(
        file.fileKey,
        `${this.filePrefix ? this.filePrefix + '-' : ''}${file.fileName}.${
          file.fileExtension
        }`
      )
      .pipe(
        takeUntil(this.loadingFiles$.pipe(filter((d) => !d[file.fileKey]))),
        tap((event) =>
          this.filesState.dispatch(
            new UpdateProgress(file.fileKey, event.percentageComplete)
          )
        ),
        finalize(() => {
          this.downloadedFiles[file.fileKey] = true;
          this.filesState.dispatch(new Delete(file.fileKey));

          this.analyticsEventsService.sendEvent('download', {
            event_category: 'expert_files',
            event_label: file.type,
            value: file.fileId,
          });
        })
      )
      .subscribe();
  }

  private groupFiles() {
    const callActions = this.actions.flatMap((a) =>
      a.actionType === 'call' ? [a] : []
    );

    const groupings = callActions
      .sort(
        (a, b) =>
          new Date(a.datetime).getTime() - new Date(b.datetime).getTime()
      )
      .map((action) => ({
        action,
        files: this.files
          .filter((file) => file.groupId === action.expertActionId)
          .sort(this.sortFilesFn),
      }))
      .filter((group) => group.files.length);

    if (groupings.length <= 1) {
      this.presentationalFiles = [];
      this.uncategorisedFiles = this.files.sort(this.sortFilesFn);
      return;
    }

    const groupIdsInPresentationalFiles = groupings
      .flatMap((group) => group.files)
      .map((file) => file.groupId);

    this.presentationalFiles = groupings.map((group, i) => ({
      groupId: group.action.expertActionId,
      label: `Call ${i + 1} (${new Date(
        group.action.datetime
      ).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })})`,
      files: group.files,
    }));

    this.uncategorisedFiles = this.files
      .filter(
        (file) =>
          !file.groupId || !groupIdsInPresentationalFiles.includes(file.groupId)
      )
      .sort(this.sortFilesFn);
  }

  private sortFilesFn(a: ExpertFile, b: ExpertFile): number {
    const order: FileType[] = [
      'enhancedTranscript',
      'zoomRecording',
      'transcriptSummary',
      'screenerResponse',
    ];

    const aIndex = order.indexOf(a.type);
    const bIndex = order.indexOf(b.type);

    if (aIndex === -1 && bIndex === -1) {
      return a.fileName.localeCompare(b.fileName);
    }

    if (aIndex === -1) {
      return 1;
    }

    if (bIndex === -1) {
      return -1;
    }

    return aIndex - bIndex;
  }
}
